import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';

import { openWerklijnInMwd } from '@/apps/mwd-v2/hooks/openWerklijnInMwd';
import useWerklijnen from '@/apps/mwd-v2/hooks/useWerklijnen';
import { MwdDeeltraject } from '@/apps/mwd-v2/types/Domain';
import { transformStations } from '@/components/detailPanel/utils/utils';
import { mapDetailInzetDirect } from '@/queries/queryModels/detailInzetModel';
import { useGetQuery } from '@/queries/useGetQuery';
import useClientState from '@/state/useClientState';

export const useDetailPanel = () => {
  const showDetailPanel = useClientState((state) => state.showDetailPanel);
  const hideDetailPanel = useClientState((state) => state.hideDetailPanel);
  const showContextMenu = useClientState((state) => state.contextMenu.showContextMenu);
  const selectedDeeltraject = useClientState((state) => state.selected.deeltrajecten[0]);
  const setDetailPanelData = useClientState((state) => state.setDetailPanelData);
  const detailPanelData = useClientState((state) => state.detailPanelData);
  const selectDeeltraject = useClientState((state) => state.selectDeeltraject);
  const [mwdDeeltraject, setMwdDeeltraject] = useState<MwdDeeltraject | undefined>(undefined);
  const { werklijnen, werklijnenMap } = useWerklijnen();

  const selectedWerklijn = mwdDeeltraject?.werklijnId ? werklijnenMap.get(mwdDeeltraject.werklijnId) : undefined;

  const {
    data: panelData,
    errorMessage: error,
    fetchData: fetchDetailInzetData,
    clearData: clearDetailInzetData,
  } = useGetQuery(mapDetailInzetDirect);

  useEffect(() => {
    if (panelData && !error) {
      startTransition(() => {
        setDetailPanelData(panelData);
      });
    } else if (error) {
      hideDetailPanel();
    }
  }, [panelData, setDetailPanelData, error]);

  useEffect(() => {
    if (!showContextMenu && selectedDeeltraject) {
      setMwdDeeltraject(selectedDeeltraject);
    }
    if (!showContextMenu && selectedDeeltraject === undefined) {
      hideDetailPanel();
    }
  }, [selectedDeeltraject]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (showDetailPanel && mwdDeeltraject && !showContextMenu) {
      fetchDetailInzetData(`/inzet/detail/${mwdDeeltraject?.wleId}`);
      intervalId = setInterval(() => {
        fetchDetailInzetData(`/inzet/detail/${mwdDeeltraject?.wleId}`);
      }, 10000); // 10 seconds update interval
    } else {
      clearDetailInzetData();
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showDetailPanel, mwdDeeltraject]);

  const activiteitenOptions = useMemo(
    () =>
      transformStations(
        (detailPanelData?.route?.activiteiten || []).map((activiteit, index) => ({
          ...activiteit,
          id: activiteit.volgnummer || `generated-id-${index}`,
        })),
        detailPanelData?.conflicten || [],
      ),
    [detailPanelData],
  );

  const volgendeInzetOptions = useMemo(
    () =>
      (detailPanelData?.werklijnDeeltrajectInzetten || []).map((inzet) => ({
        activiteiten: transformStations(
          inzet.activiteiten.map((activiteit, index) => ({
            ...activiteit,
            id: activiteit.volgnummer || `generated-id-${index}`,
          })),
          detailPanelData?.conflicten || [],
        ),
        treinSamenstelling: inzet.treinSamenstelling,
        inzetPositie: inzet.inzetPositie,
        eenheid: inzet.eenheid,
        inzetId: inzet.inzetId,
      })),
    [detailPanelData],
  );

  // Find the latest aankomstTijd in activiteitenOptions
  const latestAankomst = activiteitenOptions.reduce(
    (latest, activiteit) => {
      const tijd = activiteit.aankomstUitvoeringsTijd ? new Date(activiteit.aankomstUitvoeringsTijd).getTime() : null;
      return typeof tijd === 'number' && (latest === null || tijd > latest) ? tijd : latest;
    },
    null as number | null,
  );

  // Map volgendeInzetOptions and set isVolgendeInzet
  const volgendeInzetOptionsWithFlag = volgendeInzetOptions.map((inzet) => {
    // Find the first vertrekUitvoeringsTijd in activiteiten
    const firstVertrek = inzet.activiteiten.find((a) => a.vertrekUitvoeringsTijd)?.vertrekUitvoeringsTijd;
    const firstVertrekTime = firstVertrek ? new Date(firstVertrek).getTime() : null;
    const isVolgendeInzetZelfdeRit = typeof firstVertrekTime === 'number' && firstVertrekTime >= (latestAankomst ?? 0);
    return { ...inzet, isVolgendeInzetZelfdeRit };
  });

  /**
   * Opent de inzet bij `inzetId`/`werklijnId` via `openWerklijnInMwd`. Staat de gevraagde inzet
   * nog niet op het bord (bijv. het andere deel V/A/M van de trein), dan toont die store-action
   * zelf een toast om de gebruiker te vragen die werklijn eerst op te zoeken en toe te voegen.
   */
  const handleInzetClick = useCallback(
    (inzetId?: string, werklijnId?: string) => {
      const targetWerklijnId = werklijnId ?? selectedWerklijn?.werklijnId;
      if (!targetWerklijnId) return;

      void openWerklijnInMwd(targetWerklijnId, inzetId);
    },
    [selectedWerklijn],
  );

  return {
    selectDeeltraject,
    selectedWerklijn,
    werklijnen,
    mwdDeeltraject,
    detailPanelData,
    activiteitenOptions,
    volgendeInzetOptionsWithFlag,
    hideDetailPanel,
    handleInzetClick,
  };
};
